/*
 *  Dialog view for allowing a user to create a new Fixture Device Group (factory_devicegroup) or
 *  edit the settings of an existing one.
 *
 *  The dialog allows the user to input a name for the to-be-created Fixture Device Group, as well
 *  as select the Target Production Device Group, the Production Device Group the blessed device will be 
 *  associated to (production_devicegroup).

    
    
    1) assess what we possibly have
            options.development_devicgroup
            options.pre_factoryfixture_devicegroup
            options.pre_production_devicegroup
            options.dut_devicegroup
    -) choose/confirm production devicegroup
        
    -) create the production devicegroup (since we will need this info to target our factory)
        - if error: make suggestions on form, resubmit
            data.production_devicegroup
                - will require a new name

    -) examine/change/choose what we need to set a factory up
        - if error: make suggestions on form, resubmit
            data.factoryfixture_deviceegroup
                - will require a new name

    -) examine/change/choose deployment options
    
    -) see a summary of everything that has happened
    
*/

import Radio from 'backbone.radio';
import Marionette from 'backbone.marionette';
import _ from 'underscore';
import Handlebars from 'handlebars';
import Api from '@/lib/api/api';
import DialogInterface from '@/interfaces/dialog';
import DeviceGroupList from '@/views/modules/selectable_device_group_list';
import strings_common from 'amdi18n-loader!@/nls/common';
import strings_production_devicegroup_settings from 'amdi18n-loader!@/nls/production_device_group_settings';
import strings_specific from 'amdi18n-loader!@/nls/dialogs';
import template from '@/views/dialogs/go-live.hbs';

    var strings = _.extend({}, strings_common, strings_specific, strings_production_devicegroup_settings);
    
    var MAX_NAME_LENGTH = 100;
    var MAX_DESCRIPTION_LENGTH = 300;

    var apiCh = Radio.channel('api');
    var globalCh = Radio.channel('global');

    var post_webhook = function (devicegroup, webhook_url, event, content_type) {
        var webhooks = apiCh.request('webhook');
        return new Promise(function (resolve, reject) {
            webhooks.create({
                type: 'webhook',
                attributes: {
                    event: event,
                    content_type: content_type,
                    url: webhook_url,
                },
                relationships: {
                    devicegroup: {
                        id: devicegroup.id,
                        type: devicegroup.type
                    }
                }
            }, {
                    wait: true,
                    success: resolve,
                    error: function(webhook, response){
                        reject(response);
                    }
                });
        });
    };

    var View = Marionette.View.extend(DialogInterface).extend({
        template: Handlebars.compile(template),
        ui: {
            tooltips: '[data-toggle="tooltip"]',
            load_spinner: '.loader',

            production_target_picker: '#prod-target-picker',
            pagination_controls: '.pagination',
            production_target_has_dg: '#prod-target-picker .has_dg',
            production_target_no_dg: '#prod-target-picker .no_dg',
            device_group_picker: '.device-group-picker',
            devicegroup_list: '.devicegroup-list',
            change_prod_target: '.change-prod-target',
            production_target_name: '.production-target-name',
            production_target_name_counter: '.production-target-name-counter',
            production_target_description: '.production-target-description',
            production_target_description_counter: '.production-target-description-counter',
            production_target_profile: '.production-target-profile',
            production_target_submit: '.production-target-submit',
            totp_form: '.totp-form',
            totp_input: '#totp-input',

            dut_target_picker: '#dut-target-picker',
            dut_pagination_controls: '.dut-pagination',
            dut_target_has_dg: '#dut-target-picker .has_dg',
            dut_target_no_dg: '#dut-target-picker .no_dg',
            dut_device_group_picker: '.dut-device-group-picker',
            dut_devicegroup_list: '.dut-devicegroup-list',
            change_dut_target: '.change-dut-target',
            dut_target_name: '.dut-target-name',
            dut_target_name_counter: '.dut-target-name-counter',
            dut_target_description: '.dut-target-description',
            dut_target_description_counter: '.dut-target-description-counter',
            dut_target_profile: '.dut-target-profile',
            dut_target_submit: '.dut-target-submit',

            factory_picker: '#factory-picker',
            pagination_controls_factory: '.pagination_factory',
            factory_has_dg: '#factory-picker .has_dg',
            factory_no_dg: '#factory-picker .no_dg',
            factory_device_group_picker: '.factory-device-group-picker',
            pre_factory_list:'.pre-factory-list',
            change_factory_target: '.change-factory-target',
            factory_name: '.factory-name',
            factory_name_counter: '.factory-name-counter',
            factory_description: '.factory-description',
            factory_description_counter: '.factory-description-counter',
            factory_profile: '.factory-profile',
            factory_submit: '.factory-submit',

            prod_target_settings: '#prod-target-settings',
            blessing_options: 'input[name=blessing-options]',
            blessing_option: 'input[name=blessing-options]:checked',
            blessing_url: '#blessing-url',
            blinkup_url: '#blinkup-url',
            blessing_type: '#blessing-type',
            blinkup_type: '#blinkup-type',
            blessing_content: '.blessing',
            blinkup_content: '.blinkup',
            delete_blinkup_webhook: '.delete-blinkup-webhook',
            delete_blessing_webhook: '.delete-blessing-webhook',
            settings_submit: '.settings-submit',

            summary: '#go-live-summary',
            forms: '.form-control',

        },
        regions: {
            pagination_controls: '@ui.pagination_controls',
            devicegroup_list: '@ui.devicegroup_list',

            pagination_controls_factory: '@ui.pagination_controls_factory',
            pre_factory_list:'@ui.pre_factory_list',
            deployment_settings: '.deployment-settings',
            confirm_settings: '.confirm-settings',
            summary: '@ui.summary'

        },
        events: {
            'click @ui.change_prod_target': 'show_prod_target_picker',
            'click @ui.production_target_submit': 'create_production_target',

            'click @ui.change_dut_target': 'show_dut_target_picker',
            'click @ui.dut_target_submit': 'create_dut_target',

            'click @ui.change_factory_target': 'show_factory_device_group_picker',
            'click @ui.factory_submit': 'create_factory_devicegroup',

            'click @ui.settings_submit': 'submit_settings',

            'click @ui.delete_blinkup_webhook': function (e) {
                this.ui.blinkup_url.val('');
            },
            'click @ui.delete_blessing_webhook': function (e) {
                this.ui.blessing_url.val('');
            },
            'submit form': 'handle_submit',
            'keyup @ui.production_target_name': 'validate',
            'paste @ui.production_target_name': 'validate',
            'change @ui.production_target_name': 'validate',
            'mouseup @ui.production_target_name': 'validate',
            'keyup @ui.dut_target_name': 'validate',
            'paste @ui.dut_target_name': 'validate',
            'change @ui.dut_target_name': 'validate',
            'mouseup @ui.dut_target_name': 'validate',
            'keyup @ui.factory_name': 'validate',
            'paste @ui.factory_name': 'validate',
            'change @ui.factory_name': 'validate',
            'mouseup @ui.factory_name': 'validate',

            'keyup @ui.production_target_description': 'validate',
            'paste @ui.production_target_description': 'validate',
            'change @ui.production_target_description': 'validate',
            'mouseup @ui.production_target_description': 'validate',
            'keyup @ui.dut_target_description': 'validate',
            'paste @ui.dut_target_description': 'validate',
            'change @ui.dut_target_description': 'validate',
            'mouseup @ui.dut_target_description': 'validate',
            'keyup @ui.factory_description': 'validate',
            'paste @ui.factory_description': 'validate',
            'change @ui.factory_description': 'validate',
            'mouseup @ui.factory_description': 'validate',

            'keyup @ui.totp_input': 'validate',
        },
        childViewEvents: {
            'choose:devicegroup': 'choose_devicegroup',
            'page': function (filter) {
                var list_view = this.getRegion('devicegroup_list').currentView;
                list_view.setFilter(filter);
            },
            'close:dialog': 'closeDialog'
        },
        initialize: function (options) {
            /*
             * First thing, copy any devicegroup (DG) information we are passed.
             * Second thing, as soon as possible determine what the production_target DG will be.
             *
             * production_target is required to use go live.
             *
             */
            // development info
            this.development_devicegroup;
            this.pre_production_devicegroup;
            this.pre_dut_devicegroup;
            this.pre_factoryfixture_devicegroup;
            // production info
            this.production_devicegroup;
            this.dut_devicegroup;
            this.factoryfixture_devicegroup;
            // targets;
            this.production_target;
            this.dut_target;

            if ( options.pre_factoryfixture_devicegroup ){
                this.pre_factoryfixture_devicegroup = options.pre_factoryfixture_devicegroup;
                this.production_target = this.pre_factoryfixture_devicegroup.rel.get('production_target');
                this.pre_production_devicegroup = this.pre_factoryfixture_devicegroup.rel.get('production_target');

                this.pre_dut_devicegroup = this.pre_factoryfixture_devicegroup.rel.get('dut_target');
                this.dut_target = this.pre_factoryfixture_devicegroup.rel.get('dut_target');

                if (!this.production_target.rel.get('current_deployment')) {
                    this.production_target.fetch();
                }
            }
            if ( options.development_devicegroup ) {
                this.development_devicegroup = options.development_devicegroup;
                this.production_target = this.development_devicegroup;
            } else if ( options.pre_production_devicegroup ) {
                this.pre_production_devicegroup = options.pre_production_devicegroup;
                this.production_target = options.pre_production_devicegroup;
            }
            if ( options.pre_dut_devicegroup ) {
                this.pre_dut_devicegroup = options.pre_dut_devicegroup;
                this.dut_target = options.dut_devicegroup;
            }
            this.owner = Radio.request('global', 'active_account');
            // dialog interface requirement
            this.destroyOnClose();
            // form niceity
            this.focusFirstInput();
        },
        templateContext: function(){
            let devicegroup_profiles = JSON.stringify(this.owner.devicegroup_profiles);
            if ( devicegroup_profiles ) devicegroup_profiles = JSON.parse(devicegroup_profiles);

            var data = {
                production_target: this.production_target.attributes,
                dut_target: this.dut_target.attributes,
                development_devicegroup: this.development_devicegroup && this.development_devicegroup.attributes,
                pre_production_devicegroup: this.pre_production_devicegroup && this.pre_production_devicegroup.attributes,
                pre_dut_devicegroup: this.pre_dut_devicegroup && this.pre_dut_devicegroup.attributes,
                pre_factoryfixture_devicegroup: this.pre_factoryfixture_devicegroup && this.pre_factoryfixture_devicegroup.attributes,
                production_devicegroup: this.production_devicegroup && this.production_devicegroup.attributes,
                factory_devicegroup: this.factory_devicegroup && this.factory_devicegroup.attributes,
                s : strings,
                devicegroup_profiles: devicegroup_profiles
            };

            return data;
        },
        mfa_required: function() {
            return this.options.product.rel.get('owner').get('deployment_mfa_required');
        },
        onRender: function () {
            this.ui.tooltips.tooltip();
            this.ui.factory_submit.prop('disabled', true);
            this.ui.production_target_submit.prop('disabled', true);
            this.show_slide_1();
        },
        hide_areas: function() {
            this.ui.production_target_picker.hide();
            this.ui.dut_target_picker.hide();
            this.ui.totp_form.hide();
            this.ui.factory_picker.hide();
            this.ui.prod_target_settings.hide();
            this.ui.summary.hide();
        },
        show_slide_0: function() {
            this.hide_areas();
            // New Slide that when clicked on, evaluates the selected pre_factoryfixture_devicegroup (PRE_FF)
            // meets all of the requirements for Go Live
            //
            // 1. PRE_FF has a current deployment that is promoted
            // 2. PRE_FF has a Production Target
            // 3. Production Target's current deployment is promoted
            //
            // if yes to all, then, maybe a little loading wheel and go to Slide 1
            //
            // if no to any, then show a summary of what is not correct, and maybe ability to fix it,
            // especially promoting deployments
        },
        show_slide_1: function() {
            this.hide_areas();
            this.ui.production_target_picker.show();
            if (this.mfa_required()) this.ui.totp_form.show();
            if ( this.production_target != undefined ) {
                this.ui.production_target_has_dg.show();
                this.ui.production_target_no_dg.hide();
                this.ui.change_prod_target.show();
                this.ui.device_group_picker.hide();
            } else {
                this.ui.production_target_has_dg.hide();
                this.ui.production_target_no_dg.show();
                this.ui.production_target_picker.show();
                this.ui.change_prod_target.show();
                this.ui.device_group_picker.show();
                this.show_prod_target_picker();
            }
            if ( !this.production_target
                 && this.ui.production_target_name.val()
                 && this.ui.production_target_name.val() === this.production_target.get('name')) {
                this.ui.production_target_submit.prop('disabled', true);
            }
        },
        show_prod_target_picker: function() {
            var regions = Radio.request('api', 'cloud_regions');
            var show_regions = regions && regions.length;
            this.options.devicegroups.fetch().then(
                function(){
                    var production_target_list_view = new DeviceGroupList({
                        collection: this.options.devicegroups,
                        product: this.options.product,
                        devicegroup: this.options.pre_factoryfixture_devicegroup,
                        selected: this.production_target,
                        columns: _.compact(['select', 'name', show_regions && 'region', 'deployment']),
                        type: 'pre_production_devicegroup'
                    });
                    this.showChildView('devicegroup_list', production_target_list_view);
                    this.showChildView('pagination_controls', this.options.pagination_controls);
                    this.ui.production_target_has_dg.hide();
                    this.ui.production_target_no_dg.show();
                    this.ui.device_group_picker.show();
                }.bind(this),
                function(error){
                    this.showAlert('alert-danger', error);
                }.bind(this)
            );
        },
        choose_devicegroup: function (devicegroup) {
            if ( !devicegroup ) return;

            switch ( devicegroup.type ) {
                case 'pre_factoryfixture_devicegroup':
                    this.choose_factory(devicegroup);
                    break;
                case 'factoryfixture_devicegroup':
                    this.choose_factory(devicegroup);
                    break;
                case 'pre_dut_devicegroup':
                    this.choose_dut_target(devicegroup);
                    break;
                case 'dut_devicegroup':
                    this.choose_dut_target(devicegroup);
                    break;
                default:
                    this.choose_production_target(devicegroup);
            }
        },
        choose_production_target: function(devicegroup) {
            this.production_target = devicegroup;
            this.ui.production_target_name.val(this.production_target.get('name'));
            this.ui.production_target_description.val(this.production_target.get('description'));
            this.show_slide_1();
        },
        choose_dut_target: function(devicegroup) {
            this.dut_target = devicegroup;
            this.ui.dut_target_name.val(this.dut_target.get('name'));
            this.ui.dut_target_description.val(this.dut_target.get('description'));
            this.show_slide_2();
        },
        choose_factory: function(devicegroup){
            this.pre_factoryfixture_devicegroup = devicegroup;
            this.ui.factory_name.val(this.pre_factoryfixture_devicegroup.get('name'));
            this.ui.factory_description.val(this.pre_factoryfixture_devicegroup.get('description'));
            this.show_slide_3();
        },
        create_production_target: function(e) {
            e.preventDefault();
            var devicegroups = this.options.devicegroups;
            var product = this.options.product;
            let options = {
                product: product,
                wait: true,
                success: function(model, response){
                    this.production_devicegroup = model;
                    this.update_production_deployment();
                    this.showAlert('success', "Created Production Device Group: " + model.get('name'));
                }.bind(this),
                error: function (model, response) {
                    this.on_error(response.responseJSON.errors[0].detail);
                }.bind(this),
            };

            if ( this.owner.devicegroup_profiles ) {
                options.profile = {
                    id: this.ui.production_target_profile.val(),
                    type: 'devicegroup_profile'
                };
            }

            devicegroups.create({
                name: this.ui.production_target_name.val().trim(),
                description: this.ui.production_target_description.val().trim(),
                type: devicegroups.TYPES_MAP.production
            }, options);
        },

        update_production_deployment() {
            this.pre_production_devicegroup.rel.get('current_deployment').fetch().then(
                function() {
                    var deployments_collection = apiCh.request('deployment');
                    var attributes = {
                        device_code: this.pre_production_devicegroup.rel.get('current_deployment').get('device_code'),
                        agent_code:  this.pre_production_devicegroup.rel.get('current_deployment').get('agent_code'),
                        flagged: true
                    };
                    var headers = {};

                    if (this.mfa_required()){
                        headers = {
                            'x-electricimp-otp-token': this.ui.totp_input.val()
                        };
                    }

                    deployments_collection.create(
                    {
                        type: 'deployment',
                        attributes: attributes,
                        relationships: {
                            devicegroup: {
                                type: this.production_devicegroup.type,
                                id: this.production_devicegroup.get('id'),
                            }
                        }
                    },
                    {
                        wait: true,
                        headers: headers,
                        url: Api.urls.deployments_nocode({ nocode: true }),
                        success: function(deployment) {
                            this.production_devicegroup.rel.set('current_deployment', deployment);
                            this.show_slide_2();
                        }.bind(this),
                        error: function(model, response) {
                           this.production_devicegroup.destroy();
                           this.on_error(response.responseJSON.errors[0].detail);
                        }.bind(this)
                    }
                );
                }.bind(this),
                function(err) {
                    this.on_error(err.responseJSON.errors[0].detail);
                    this.production_devicegroup.destroy();
                }.bind(this)
            );
        },

        show_slide_2: function() {
            this.hide_areas();
            this.ui.dut_target_picker.show();
            if ( this.dut_target != undefined ) {
                this.ui.dut_target_has_dg.show();
                this.ui.dut_target_no_dg.hide();
                this.ui.change_dut_target.show();
                this.ui.dut_device_group_picker.hide();
            } else {
                this.ui.dut_target_has_dg.hide();
                this.ui.dut_target_no_dg.show();
                this.ui.dut_target_picker.show();
                this.ui.change_dut_target.show();
                this.ui.dut_group_picker.show();
                this.show_dut_target_picker();
            }
            if ( !this.dut_target
                 && this.ui.dut_target_name.val()
                 && this.ui.dut_target_name.val() === this.dut_target.get('name')) {
                this.ui.dut_target_submit.prop('disabled', true);
            }
        },
        create_dut_target: function(e) {
            e.preventDefault();
            var devicegroups = this.options.devicegroups;
            var product = this.options.product;
            let options = {
                product: product,
                wait: true,
                success: function(model, response){
                    this.dut_target = model;
                    this.dut_devicegroup = model;
                    this.update_dut_deployment();
                    this.showAlert('success', "Created DUT Device Group: " + model.get('name'));
                }.bind(this),
                error: function (model, response) {
                    this.on_error(response.responseJSON.errors[0].detail);
                }.bind(this),
            };

            if ( this.owner.devicegroup_profiles ) {
                options.profile = {
                    id: this.ui.dut_target_profile.val(),
                    type: 'devicegroup_profile'
                };
            }

            devicegroups.create({
                name: this.ui.dut_target_name.val().trim(),
                description: this.ui.dut_target_description.val().trim(),
                type: devicegroups.TYPES_MAP.dut
            }, options);
        },

        update_dut_deployment() {
            this.pre_dut_devicegroup.rel.get('current_deployment').fetch().then(
                function() {
                    var deployments_collection = apiCh.request('deployment');
                    var attributes = {
                        device_code: this.pre_dut_devicegroup.rel.get('current_deployment').get('device_code'),
                        agent_code:  this.pre_dut_devicegroup.rel.get('current_deployment').get('agent_code'),
                        flagged: true
                    };

                    deployments_collection.create(
                    {
                        type: 'deployment',
                        attributes: attributes,
                        relationships: {
                            devicegroup: {
                                type: this.dut_target.type,
                                id: this.dut_target.id,
                            }
                        }
                    },
                    {
                        wait: true,
                        url: Api.urls.deployments_nocode({ nocode: true }),
                        success: function(deployment) {
                            this.dut_devicegroup.rel.set('current_deployment', deployment);
                            this.dut_target.rel.set('current_deployment', deployment);
                            this.show_slide_3();
                        }.bind(this),
                        error: function(model, response) {
                           this.dut_devicegroup.destroy();
                           this.on_error(response.responseJSON.errors[0].detail);
                        }.bind(this)
                    }
                );
                }.bind(this),
                function(err) {
                    this.on_error(err.responseJSON.errors[0].detail);
                    this.dut_devicegroup.destroy();
                }.bind(this)
            );
        },

        show_slide_3: function() {
            this.hide_areas();
            this.ui.factory_picker.show();

           if ( this.pre_factoryfixture_devicegroup != undefined ){
                this.ui.factory_has_dg.show();
                this.ui.factory_no_dg.hide();
                this.ui.change_factory_target.show();
                this.ui.factory_device_group_picker.hide();
            } else {
                this.ui.factory_has_dg.hide();
                this.ui.factory_no_dg.show();
                this.ui.factory_device_group_picker.show();
                this.ui.change_factory_target.hide();
                this.show_factory_device_group_picker();
            }
            if ( !this.dut_target
                 && this.ui.dut_target_name.val()
                 && this.ui.dut_target_name.val() === this.production_target.get('name')) {
                this.ui.dut_target_submit.prop('disabled', true);
            }
        },

        show_factory_device_group_picker: function() {
            var regions = Radio.request('api', 'cloud_regions');
            var show_regions = regions && regions.length;
            this.options.devicegroups.fetch().then(
                function(){
                    var factory_target_list_view = new DeviceGroupList({
                        collection: this.options.devicegroups,
                        product: this.options.product,
                        devicegroup: this.production_target,
                        selected: this.options.pre_factoryfixture_devicegroup,
                        columns: _.compact(['select', 'name', show_regions && 'region', 'deployment']),
                        type: 'pre_factoryfixture_devicegroup'
                    });
                    this.showChildView('pre_factory_list', factory_target_list_view);
                    this.showChildView('pagination_controls_factory', this.options.pagination_controls);
                    this.ui.factory_has_dg.hide();
                    this.ui.factory_no_dg.show();
                    this.ui.device_group_picker.show();
                    this.ui.change_factory_target.hide();
                    this.ui.factory_device_group_picker.show();
                }.bind(this),
                function(error){
                    this.showAlert('alert-danger', error);
                }.bind(this)
            );
        },
        create_factory_devicegroup: function(e) {
            e.preventDefault();
            var devicegroups = this.options.devicegroups;
            var product = this.production_devicegroup.rel.get('product');

            let options = {
                product: product,
                production_target: this.production_devicegroup,
                dut_target: this.dut_target,
                wait: true,
                success: function(model, response){
                    this.factory_devicegroup = model;
                    this.update_factory_deployment(model);
                    this.showAlert('success', "Created Fixture Device Group: " + model.get('name'));
                }.bind(this),
                error: function (model, response) {
                    this.on_error(response.responseJSON.errors[0].detail);
                }.bind(this),
            };

            if ( this.owner.devicegroup_profiles ) {
                options.profile = {
                    id: this.ui.factory_profile.val(),
                    type: 'devicegroup_profile'
                };
            }

            devicegroups.create({
                name: this.ui.factory_name.val().trim(),
                type: devicegroups.TYPES_MAP.factoryfixture,
                description: this.ui.factory_description.val().trim()
            }, options);
        },

        update_factory_deployment() {
            this.pre_factoryfixture_devicegroup.rel.get('current_deployment').fetch().then(
                function() {
                    let deployments_collection = apiCh.request('deployment');
                    let current_deployment = this.pre_factoryfixture_devicegroup.rel.get('current_deployment');
                    let attributes = {
                        device_code: current_deployment.get('device_code'),
                        agent_code: current_deployment.get('agent_code'),
                        flagged: true
                    };
        
                    deployments_collection.create(
                        {
                            type: 'deployment',
                            attributes: attributes,
                            relationships: {
                                devicegroup: {
                                    type: this.factory_devicegroup.type,
                                    id: this.factory_devicegroup.get('id'),
                                }
                            }
                        },
                        {
                            wait: true,
                            url: Api.urls.deployments_nocode({ nocode: true }),
                            success: function(new_deployment) {
                                this.factory_devicegroup.rel.set('current_deployment', new_deployment);
                                this.show_options(); 
                            }.bind(this),
                            error: function(model, response) {
                                this.on_error(response.responseJSON.errors[0].detail);
                            }.bind(this)
                        }
                    );
                }.bind(this));
        },
        show_options: function() {
            this.hide_areas();
            this.ui.prod_target_settings.show();
            this.pre_production_devicegroup = this.pre_factoryfixture_devicegroup.rel.get('production_target');
            this.pre_production_devicegroup.fetch().then( 
                function() {
                    this.render_webhooks();
                    this.render_load_code_after_blessing();
                }.bind(this)
            );
        },
        render_webhooks: function() {
            var webhooks = this.pre_production_devicegroup.webhooks;
            webhooks.fetch().then(function () {
                webhooks.forEach(function (webhook) {
                    if (webhook.get('event') === 'blessing') {
                        this.blessing_webhook = webhook;
                        this.ui.blessing_url.val(webhook.get('url'));
                        this.ui.blessing_type.val(webhook.get('content_type'));
                        this.ui.delete_blessing_webhook.removeClass('hidden');
                    } else if (webhook.get('event') === 'blinkup') {
                        this.blinkup_webhook = webhook;
                        this.ui.blinkup_url.val(webhook.get('url'));
                        this.ui.blinkup_type.val(webhook.get('content_type'));
                        this.ui.delete_blinkup_webhook.removeClass('hidden');
                    }
                }.bind(this));
            }.bind(this));
        },
        render_load_code_after_blessing: function(){
            if ( this.pre_production_devicegroup && _.has(this.pre_production_devicegroup.attributes, 'load_code_after_blessing')) {
                if (this.pre_production_devicegroup.get('load_code_after_blessing') === true) {
                    this.ui.blessing_options.filter(function () { return this.value == 'blessing'; }).prop('checked', true);
                    this.blessing_option = 'blessing';
                } else {
                    this.ui.blessing_options.filter(function () { return this.value == 'blinkup'; }).prop('checked', true);
                    this.blessing_option = 'blinkup';
                }
            }
        },
        submit_settings: function(e) {
            e.preventDefault();
            this.create_webhooks();
        },
        create_webhooks: function(){
            var calls = [];

            if (this.ui.blessing_url.val()){
                calls.push(post_webhook(this.production_devicegroup, this.ui.blessing_url.val(), 'blessing', this.ui.blessing_type.val()));            
            }
            if (this.ui.blinkup_url.val()){
                calls.push(post_webhook(this.production_devicegroup, this.ui.blinkup_url.val(), 'blinkup', this.ui.blinkup_type.val()));
            }

            Promise.all(calls).then(function(){
                this.update_blessing_option(this.production_devicegroup).then(
                        function() {
                            this.showAlert('success', "Production & Fixture Device Groups Created & Configured");
                            this.confirm_go_live_settings();
                        }.bind(this)
                    );
            }.bind(this),
            function(e){ this.on_error(e.responseJSON.errors[0].detail); }.bind(this));
        },
        update_blessing_option: function(model){
            var load_code_after_blessing = this.ui.blessing_options.filter(function () { return this.value == 'blessing'; }).prop('checked');
            let data = {
                        id: model.id,
                        type: model.type,
                        attributes: {
                            load_code_after_blessing: load_code_after_blessing,
                        }
                    };
            if (model.rel.get('profile')) {
                data.relationships = {
                    profile: {
                        id: model.rel.get('profile').id,
                        type: model.rel.get('profile').type
                    }
                };
            }
            return new Promise(function(resolve, reject){
                var production_devicegroup = model;
                production_devicegroup.save({
                    data: data 
                },
                    {
                        patch: true,
                        wait: true,
                        success: resolve,
                        error: reject,
                    });
            }.bind(this));
        },
        confirm_go_live_settings: function(e) {
            var self = this;
            this.hide_areas();
            this.ui.prod_target_settings.hide();
            this.ui.summary.show();

            var SummaryTemplate = `<h5>New Production Device Group</h5>
                <div class="row">
                    <label class="col-xs-4">Name:</label><span class="col-xs-8 btn-link text-left production-devicegroup">{{production_devicegroup.name}}</span>
                </div>
                <div class="row">
                    <label class="col-xs-4">Description:</label><span class="col-xs-8">{{production_devicegroup.description}}</span>
                </div>
                {{#if devicegroup_profiles}}
                    <div class="row"><label class="col-xs-4">Profile:</label><span class="col-xs-8">{{production_devicegroup_profile}}</span></div>
                {{/if}}
                <div class="row"><label class="col-xs-4">Deployment:</label><span class="col-xs-8">{{short_sha production_deployment.sha}}</span></div>
                <div class="row"><label class="col-xs-4">Download Application:</label><span class="col-xs-8">
                {{#if download_app}}After Blessing{{else}}After Blinkup{{/if}}</span></div>
                <div class="row"><label class="col-xs-4">Blessing Webhook:</label><span class="col-xs-8">{{blessing_webhook.url}}</span></div>
                <div class="row"><label class="col-xs-4">BlinkUp:</label><span class="col-xs-8">{{blinkup_webhook.url}}</span></div>
                <h5>New DUT Device Group</h5>
                <div class="row"><label class="col-xs-4">Name:</label><span class="col-xs-8 btn-link dut-devicegroup">{{dut_devicegroup.name}}</span></div>
                <div class="row"><label class="col-xs-4">Description:</label><span class="col-xs-8">{{dut_devicegroup.description}}</span></div>
                {{#if devicegroup_profiles}}
                    <div class="row"><label class="col-xs-4">Profile:</label><span class="col-xs-8">{{dut_devicegroup_profile}}</span></div>
                {{/if}}
                <h5>New Fixture Device Group</h5>
                <div class="row"><label class="col-xs-4">Name:</label><span class="col-xs-8 btn-link factory-devicegroup">{{factory_devicegroup.name}}</span></div>
                <div class="row"><label class="col-xs-4">Description:</label><span class="col-xs-8">{{factory_devicegroup.description}}</span></div>
                {{#if devicegroup_profiles}}
                    <div class="row"><label class="col-xs-4">Profile:</label><span class="col-xs-8">{{factory_devicegroup_profile}}</span></div>
                {{/if}}
                <div class="row"><label class="col-xs-4">Deployment:</label><span class="col-xs-8">{{short_sha factory_deployment.sha}}</span></div>
                <div class="row"><div class="col-xs-12 text-center"><button class="btn btn-primary cancel" data-dismiss="modal">Done</button></div</div>`;

            var view = new Marionette.View({
                template: Handlebars.compile(SummaryTemplate),
                ui : {
                    production_devicegroup_link : '.production-devicegroup',
                    dut_devicegroup_link : '.dut-devicegroup',
                    factory_devicegroup_link : '.factory-devicegroup',
                },
                events : {
                    'click @ui.production_devicegroup_link' : function(){
                        globalCh.request('show_devicegroup_dashboard', self.production_devicegroup );
                        this.triggerMethod('close:dialog');
                    },
                    'click @ui.dut_devicegroup_link' : function() { 
                        globalCh.request('show_devicegroup_dashboard', self.dut_devicegroup );
                        this.triggerMethod('close:dialog');
                    },
                    'click @ui.factory_devicegroup_link' : function() { 
                        globalCh.request('show_devicegroup_dashboard', self.factory_devicegroup );
                        this.triggerMethod('close:dialog');
                    }
                },
                templateContext: function(){
                    var production_devicegroup = self.production_devicegroup;
                    var dut_devicegroup = self.dut_devicegroup;
                    var factory_devicegroup = self.factory_devicegroup;
                    var production_deployment = production_devicegroup.rel.get('current_deployment');
                    var dut_deployment = dut_devicegroup.rel.get('current_deployment');
                    var factory_deployment = factory_devicegroup.rel.get('current_deployment') || { attributes: { name: 'undefined' } };
                    var webhooks = production_devicegroup.webhooks;
                    var blessing_webhook = webhooks.findWhere({ event: 'blessing' });
                    var blinkup_webhook = webhooks.findWhere({ event: 'blinkup' });
                    if ( blessing_webhook === undefined) {
                        blessing_webhook = { attributes: { url: 'No Blessing Webhook' } };
                    }
                    if ( blinkup_webhook === undefined) {
                        blinkup_webhook = { attributes: { url: 'No Blinkup Webhook' } };
                    }
                    let devicegroup_profiles = self.owner.devicegroup_profiles;
                    let production_devicegroup_profile = undefined;
                    let dut_devicegroup_profile = undefined;
                    let factory_devicegroup_profile = undefined;
                    if (devicegroup_profiles) {
                        production_devicegroup_profile = production_devicegroup.rel.get('profile');
                        if (production_devicegroup_profile) {
                            let production_profile = self.owner.devicegroup_profiles.find(
                                function(p){
                                    return p.id === production_devicegroup_profile.id;
                                }
                            );
                            if ( !production_profile ){
                                 production_devicegroup_profile = 'Default';
                            } else {
                                 production_devicegroup_profile = production_profile.attributes.name;
                            }
                        } else {
                            production_devicegroup_profile = 'Default';
                        }
                        
                        dut_devicegroup_profile = dut_devicegroup.rel.get('profile');
                        if (dut_devicegroup_profile) {
                            let dut_profile = self.owner.devicegroup_profiles.find(
                                function(p){
                                    return p.id === dut_devicegroup_profile.id;
                                }
                            );
                            if ( !dut_profile ){
                                 dut_devicegroup_profile = 'Default';
                            } else {
                                 dut_devicegroup_profile = dut_profile.attributes.name;
                            }
                        } else {
                            dut_devicegroup_profile = 'Default';
                        }


                        factory_devicegroup_profile = factory_devicegroup.rel.get('profile');
                        if (factory_devicegroup_profile) {
                            let factory_profile = self.owner.devicegroup_profiles.find(
                                function(p){
                                    return p.id === factory_devicegroup_profile.id;
                                }
                            );
                            if ( !factory_profile ){
                                 factory_devicegroup_profile = 'Default';
                            } else {
                                 factory_devicegroup_profile = factory_profile.attributes.name;
                            }
                        } else {
                            factory_devicegroup_profile = 'Default';
                        }
                    }
                    return {
                        production_devicegroup: production_devicegroup.attributes,
                        production_devicegroup_profile: production_devicegroup_profile,
                        production_deployment: production_deployment.attributes,
                        dut_devicegroup: dut_devicegroup.attributes,
                        dut_devicegroup_profile: dut_devicegroup_profile,
                        dut_deployment: dut_deployment.attributes,
                        factory_devicegroup: factory_devicegroup.attributes,
                        factory_devicegroup_profile: factory_devicegroup_profile,
                        factory_deployment: factory_deployment.attributes,
                        blessing_webhook: blessing_webhook.attributes,
                        blinkup_webhook: blinkup_webhook.attributes,
                        download_app: production_devicegroup.get('load_code_after_blessing'),
                        devicegroup_profiles: devicegroup_profiles
                    };
                },
            });
            this.showChildView('summary', view);
        },

        on_success: function (new_factory_devicegroup, message) {
            Radio.request('alerts', 'showAlert', {
                message: message,
                messageType: 'success'
            });
            this.closeDialog();
        },
        on_error: function (error) {
            this.showAlert('alert-danger', error);
        },
        validate: function (e) {
            this.check_validation(
                this.ui.factory_name,
                MAX_NAME_LENGTH,
                this.ui.factory_name_counter,
                this.ui.submit_btn
            );

            this.check_validation(
                this.ui.production_target_name,
                MAX_NAME_LENGTH,
                this.ui.production_target_name_counter,
                this.ui.production_target_submit
            );

            this.check_validation(
                this.ui.dut_target_name,
                MAX_NAME_LENGTH,
                this.ui.dut_target_name_counter,
                this.ui.dut_target_submit
            );

            this.check_validation(
                this.ui.factory_description,
                MAX_DESCRIPTION_LENGTH,
                this.ui.factory_description_counter,
                this.ui.submit_btn
            );

            this.check_validation(
                this.ui.production_target_description,
                MAX_DESCRIPTION_LENGTH,
                this.ui.production_target_description_counter,
                this.ui.production_target_submit
            );

            this.check_validation(
                this.ui.dut_target_description,
                MAX_DESCRIPTION_LENGTH,
                this.ui.dut_target_description_counter,
                this.ui.dut_target_submit
            );

            if ( this.ui.production_target_name.val().length > 0 ) {
                this.ui.production_target_submit.prop('disabled', false);
            } else {
                this.ui.production_target_submit.prop('disabled', true);
            }

            if ( this.ui.dut_target_name.val().length > 0 ) {
                this.ui.dut_target_submit.prop('disabled', false);
            } else {
                this.ui.dut_target_submit.prop('disabled', true);
            }

            if ( this.mfa_required() && this.ui.totp_input.val().length != 6 ) {
                this.ui.production_target_submit.prop('disabled', true);
            }

            if ( this.ui.factory_name.val().length > 0 ) {
                this.ui.factory_submit.prop('disabled', false);
            } else {
                this.ui.factory_submit.prop('disabled', true);
            }
        },

    });
    export default View;


